import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { auth } from "../../config/firebase";
import { Eye, EyeOff } from "lucide-react";

import illustrationLight from "../../assets/images/illustration-light.png";
import logoOriginal from "../../assets/images/logo_frotabaase_transp.png";

const LoginPage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const brandColor = "#00a99d";

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .matches(/^[^\s@]+@[^\s@]+$/, "Formato de e-mail inválido.")
        .required("O e-mail é obrigatório"),
      password: Yup.string()
        .min(6, "A senha deve ter pelo menos 6 caracteres")
        .required("A senha é obrigatória"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setError(null);
      setSubmitting(true);
      try {
        await setPersistence(auth, browserSessionPersistence);
        const userCredential = await signInWithEmailAndPassword(
          auth,
          values.email,
          values.password
        );
        if (userCredential.user.email === "kadoflorence@gmail.com") {
          navigate("/super-admin");
        } else {
          navigate("/");
        }
      } catch (error) {
        setError("Falha ao fazer login. Verifique suas credenciais.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="flex min-h-screen w-full font-sans bg-white dark:bg-dark-bg overflow-hidden">
      
      {/* Lado Esquerdo: Identidade Visual Familiar (Desktop / Ultrawide) */}
      <div className="hidden lg:flex relative w-1/2 flex-col justify-between overflow-hidden shadow-2xl z-10">
        <img 
          src={illustrationLight} 
          alt="Background Real Benefícios" 
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        <div className="relative z-10 p-12 2xl:p-20">
          <img 
            src={logoOriginal} 
            alt="Logo Real Benefícios" 
            className="h-16 md:h-20 w-auto object-contain drop-shadow-sm" 
          />
        </div>

        <div className="relative z-10 p-12 2xl:p-20 text-center pb-20">
          <p className="text-white/80 mt-4 text-sm font-medium">@ 2025 - Real Benefícios</p>
        </div>
      </div>

      {/* Lado Direito: Formulário de Acesso */}
      <div className="flex flex-1 justify-center items-center px-6 py-8 sm:px-12 lg:p-24 2xl:p-32 relative z-0">
        <div className="w-full max-w-md animate-fade-in">
          
          {/* Logo EXCLUSIVA para Mobile / Tablet (Garante que o usuário sabe onde está) */}
          <div className="lg:hidden flex justify-center mb-10">
            <img 
              src={logoOriginal} 
              alt="Logo Real Benefícios" 
              className="h-14 sm:h-16 w-auto object-contain drop-shadow-sm" 
            />
          </div>

          <h1 className="text-2xl sm:text-3xl lg:text-4xl leading-tight font-light text-gray-800 dark:text-white mb-8 lg:mb-16 text-center lg:text-left">
            Faça mais e melhor, com a <br className="hidden sm:block" />
            <span className="font-bold">nossa plataforma de gestão.</span>
          </h1>

          <div className="mb-8 lg:mb-10 text-center lg:text-left">
            <h2 className="text-xl sm:text-2xl font-bold mb-1" style={{ color: brandColor }}>
              Acesse sua conta
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Digite seu login para entrar
            </p>
          </div>

          <form onSubmit={formik.handleSubmit} noValidate className="space-y-8 lg:space-y-10">
            
            {/* Campo E-mail */}
            <div className="relative flex flex-col gap-1">
              <label className="text-xs text-gray-400 font-medium uppercase tracking-wider">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                {...formik.getFieldProps("email")}
                className={`w-full py-3 bg-transparent border-b-2 text-gray-800 dark:text-white focus:outline-none transition-all duration-300 ${
                  formik.touched.email && formik.errors.email
                    ? "border-red-500"
                    : "border-gray-300 dark:border-dark-border focus:border-[#00a99d]"
                }`}
              />
              {formik.touched.email && formik.errors.email && (
                <p className="absolute -bottom-5 left-0 text-[10px] text-red-500 uppercase font-bold tracking-tighter">
                  {formik.errors.email}
                </p>
              )}
            </div>

            {/* Campo Senha */}
            <div className="relative flex flex-col gap-1">
              <label className="text-xs text-gray-400 font-medium uppercase tracking-wider">Senha</label>
              <div className="relative w-full">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  {...formik.getFieldProps("password")}
                  className={`w-full py-3 pr-12 bg-transparent border-b-2 text-gray-800 dark:text-white focus:outline-none transition-all duration-300 ${
                    formik.touched.password && formik.errors.password
                      ? "border-red-500"
                      : "border-gray-300 dark:border-dark-border focus:border-[#00a99d]"
                  }`}
                />
                {/* Botão do Olho com hit-box maior e centralizado na altura do input */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 transform -translate-y-1/2 p-2 text-gray-400 hover:text-[#00a99d] transition-colors flex items-center justify-center"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {formik.touched.password && formik.errors.password && (
                <p className="absolute -bottom-5 left-0 text-[10px] text-red-500 uppercase font-bold tracking-tighter">
                  {formik.errors.password}
                </p>
              )}
            </div>

            {/* Box de Erro do Firebase */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-100 dark:border-red-800 animate-fade-in">
                <p className="text-xs text-red-600 dark:text-red-400 font-bold text-center italic">{error}</p>
              </div>
            )}

            {/* Botão e Link */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-4">
              <button
                type="submit"
                disabled={formik.isSubmitting}
                className="w-full sm:w-auto py-3 md:py-4 px-14 rounded-full text-white font-bold transition-all duration-300
                focus:outline-none disabled:opacity-50 hover:scale-105 shadow-xl shadow-[#00a99d]/20 active:scale-95"
                style={{ backgroundColor: brandColor }} 
              >
                {formik.isSubmitting ? "Autenticando..." : "Entrar"}
              </button>

              <Link
                to="/esqueci-minha-senha"
                className="text-sm font-semibold hover:opacity-70 transition-opacity"
                style={{ color: brandColor }}
              >
                Esqueci minha senha
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;